import { MenuItem, MenuItemText, Stacked, Toggle } from '@statsbomb/kitbag-components';

import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtom, useAtomValue } from 'jotai';
import { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { sectionAtom } from '@/atoms/section';
import { selectedVisualisationAtom, showLegendAtom } from '@/atoms/visualisation';
import { useExportInfo } from '@/hooks/exporting/useExportInfo';
import { snakeToCamel } from '@/utils/queries';
import { SettingsButton } from '../button/SettingsButton';
import { MoreOptionsMenuFallback } from '../menu/MoreOptionsMenu/MoreOptionsMenu';
import { PlayAllButton } from '../video/PlayAllButton';
import { PitchTypeToggle } from '../visualisations/PitchTypeToggle';
import { VisualisationMenu } from '../visualisations/VisualisationMenu';

const VisControlsFallback = () => (
  <Stacked>
    <PlayAllButton />
    <SettingsButton />
    <MoreOptionsMenuFallback />
  </Stacked>
);

const VisControlsWithData = ({ settingsButton }: { settingsButton: ReactNode }) => {
  const { t } = useTranslation(['visualisation', 'events', 'metrics', 'export']);
  const selectedVis = useAtomValue(selectedVisualisationAtom);
  const section = useAtomValue(sectionAtom);
  const [{ metric: selectedMetricKey, eventNames: selectedEvents }] = useFilters('main');
  const entitySecondaryTitle = selectedMetricKey ? t(`${snakeToCamel(selectedMetricKey)}.name`, { ns: 'metrics' }) : '';
  const entitySecondaryTitleLabel = entitySecondaryTitle ? t('metricLabel', { ns: 'export' }) : '';
  const translatedSelectedEvents = selectedEvents.map(key => ({
    label: t(`fields.event.type.${snakeToCamel(key)}`, { ns: 'events' }),
    value: key,
  }));
  const dataLockerSecondaryTitle = useExportInfo(translatedSelectedEvents, 'Events', 3);
  const dataLockerTitlePrefix = t('eventsLabel', { ns: 'export' });
  const templateName = section === 'dataLocker' ? dataLockerSecondaryTitle : entitySecondaryTitle;
  const secondaryTitlePrefix = section === 'dataLocker' ? dataLockerTitlePrefix : entitySecondaryTitleLabel;

  const [showLegend, setShowLegend] = useAtom(showLegendAtom);

  return (
    <Stacked testId="vis-controls">
      <PlayAllButton />
      <Suspense>
        <PitchTypeToggle />
      </Suspense>
      {settingsButton}
      <VisualisationMenu
        exportModalProps={{
          visName: t(selectedVis.visualisationKey, { ns: 'visualisation' }),
          templateName,
          secondaryTitlePrefix,
        }}
        keysToExcludeFromClose={['showLegend']}
      >
        <MenuItem key="showLegend">
          <MenuItemText>{t('showLegend')}</MenuItemText>
          <Toggle
            id="legend-toggle"
            checked={showLegend}
            onChange={() => setShowLegend(!showLegend)}
            hasLabel={false}
            size="small"
            label={t('showLegend')}
          />
        </MenuItem>
      </VisualisationMenu>
    </Stacked>
  );
};

export const VisControls = ({ settingsButton = <SettingsButton /> }: { settingsButton?: ReactNode }) => (
  <Suspense fallback={<VisControlsFallback />}>
    <VisControlsWithData settingsButton={settingsButton} />
  </Suspense>
);
